/**
 * Pure date utilities - NO Angular dependencies
 * Can be used in both Angular apps and Firebase Cloud Functions
 *
 * GMT is now known as UTC:
 * Prior to 1972, this time was called Greenwich Mean Time (GMT) but is now referred to as
 * Coordinated Universal Time or Universal Time Coordinated (UTC). It is a coordinated time
 * scale, maintained by the Bureau International des Poids et Mesures (BIPM).
 *
 * ISO:
 * The International Organization for Standardization (ISO) date and time format is a standard
 * way to express a numeric calendar date -- and optionally time -- in a format that eliminates
 * ambiguity between entities. The format, defined in the ISO 8601 standard, is used extensively
 * by applications and machines to exchange date and time data without the uncertainly that comes
 * when trying to communicate this data across international boundaries, diverse cultures or
 * different time zones.  For example, North Americans usually write the month before the date,
 * as in March 30, 2021 or 3/30/2021. However, Europeans typically write the date before the month,
 * as in 30 March 2021 or 30.3.2021. Even the separators between the numbers can vary. ISO 8601
 * provides an international standard that eliminates this type of confusion.  Date representations
 * in ISO 8601 are based on the Gregorian calendar, which replaced the Julian calendar in 1582 to
 * better accommodate the seasonal cycles. The ISO standard also considers leap years. Additionally,
 * it is based on a 24-hour clock (sometimes referred to as military time), and it accommodates
 * varying time zones and how they differ from Coordinated Universal Time (UTC).
*/
// @firebase/firestore-types'
// https://javascript.plainenglish.io/date-manipulation-in-js-made-easy-with-date-fns-6c66706a5874
import {
    addSeconds, addMinutes, addHours, addDays, addMonths, addYears, setHours, setMinutes,
    setSeconds, intervalToDuration, formatDuration, compareAsc, format, formatISO, parse,
    parseISO, isAfter, formatISODuration
} from 'date-fns';

import type { Duration } from 'date-fns';
// import { format as formatTZ } from 'date-fns-tz';

import { Timestamp } from 'firebase/firestore';
import type { TTimestamp } from '@shared/models';
import { Constants } from '@shared/models';
const DateTimeFormats = Constants.DateTimeFormats;

/*
export class DateUtils {
    
    public static now(): Timestamp {
        return Timestamp.now();
    }

    public static fromDate(date: Date): Timestamp {
        return Timestamp.fromDate(date);
    }

    public static toDate(timestamp: Timestamp): Date {
        return timestamp.toDate();
    }

    public static addDays(timestamp: Timestamp, days: number): Timestamp {
        const date = timestamp.toDate();
        date.setDate(date.getDate() + days);
        return Timestamp.fromDate(date);
    }

    static addMonths(timestamp: Timestamp, months: number): Timestamp {
        const date = timestamp.toDate();
        date.setMonth(date.getMonth() + months);
        return Timestamp.fromDate(date);
    }

    static formatDate(timestamp: Timestamp, format: string = 'YYYY-MM-DD'): string {
        const date = timestamp.toDate();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        return format
            .replace('YYYY', String(year))
            .replace('MM', month)
            .replace('DD', day);
    }

    static daysBetween(start: Timestamp, end: Timestamp): number {
        const diffMs = end.toMillis() - start.toMillis();
        return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    }

    static isPast(timestamp: Timestamp): boolean {
        return timestamp.toMillis() < Date.now();
    }

    static isFuture(timestamp: Timestamp): boolean {
        return timestamp.toMillis() > Date.now();
    }
}

// There is a good argument to be made that date.service.ts should be in the libs/common folder
// Consider refactoring to make all date handling be done in the UTC timezone.


// type Duration = {
//     years: number;
//     months: number;
//     days: number;
//     hours: number;
//     minutes: number;
//     seconds: number;
// };
*/
export class DateImplService {
    public static localTimezone: string;
    // private minimumDate = new Date('0001-01-01T00:00:00Z');
    // private maximumDate = new Date('2050-12-30T23:59:59Z');

    private static isoMinimumDate = '0001-01-01T00:00:00Z';
    private static isoMaximumDate = '2050-12-30T23:59:59Z';

    private static tsMinDate: TTimestamp | null = null;
    private static tsMaxDate: TTimestamp | null = null;

    private static MILLISECS_PER_SECOND = 1000;

    // Lazy initialization to avoid running code during module load (fixes Firebase deployment timeout)
    private static initializeIfNeeded(): void {
        if (!this.tsMinDate) {
            this.tsMinDate = this.UTCToTimestamp(this.isoMinimumDate);
            this.tsMaxDate = this.UTCToTimestamp(this.isoMaximumDate);
        }
    }

    /**
     * Returns the minimum date timestamp (0001-01-01).
     * Initialization happens automatically on first access.
     */
    public static get tsMinimumDate(): TTimestamp {
        this.initializeIfNeeded();
        return this.tsMinDate!;
    }

    /**
     * Returns the maximum date timestamp (2050-12-30).
     * Initialization happens automatically on first access.
     */
    public static get tsMaximumDate(): TTimestamp {
        this.initializeIfNeeded();
        return this.tsMaxDate!;
    }

    public static createTimestamp(seconds = 0, nanoseconds = 0): TTimestamp {
        if (seconds === 0) {
            const now = this.now();
            const utc = this.timestampToUTC(now);
            const date = new Date(utc);
            seconds = date.getTime() / this.MILLISECS_PER_SECOND;
        }

        const timestamp = new Timestamp(seconds, nanoseconds) as TTimestamp;
        return timestamp;
    }

    public static now(): TTimestamp {
        const now = Date.now();
        const tsNow = this.toTimestamp(now) as TTimestamp;
        return tsNow;
    }

    public static isMinimumDate(ts: TTimestamp): boolean {
        if (!ts || ts.seconds === 0) {
            return true;
        }
        const isEqual = (ts.seconds === this.tsMinimumDate.seconds);
        return isEqual;
    }

    // If using cloud function:  admin.firestore.Timestamp.fromDate()
    // public static toTimestamp(dt: Date | number | string): TTimestamp {
    // create a timestamp from a javascript date, number or string

    public static toTimestamp(dt: Date | number | string): TTimestamp {
        let timestamp: Timestamp;
        const date2 = new Date(dt);

        switch (typeof dt) {
            case 'number':
                timestamp = Timestamp.fromMillis(dt);
                break;

            case 'string':
                timestamp = Timestamp.fromDate(date2);
                break;

            case 'object':
                if (dt instanceof Date) {
                    timestamp = Timestamp.fromDate(dt) as TTimestamp;
                } else {
                    throw new Error('Invalid parameter passed to toTimestamp()');
                }
                break;
        }
        return timestamp as TTimestamp;
    }

    public static toDate(timestamp: TTimestamp): Date {
        if (!timestamp) {
            throw new Error('Invalid parameter passed to toDate()');
        }

        // if (typeof timestamp.toDate === 'undefined') {
        //     // const dt: Date = timestamp.toDate();

        //     // const seconds = timestamp.seconds;
        //     // const seconds = (timestamp as any)._seconds;
        //     // const nanoseconds = (timestamp as any)._nanoseconds;
        //     // const yyy = this.createTimestamp(seconds, nanoseconds);
        //     // const date = yyy.toDate();
        //     const date = timestamp.toDate();

        //     // const ts = this.toTimestamp(seconds);
        //     // const date = this.toDate(ts);

        //     // const xxx = new Timestamp(seconds, nanoseconds);
        //     // const date = xxx.toDate();
        //     // const date = this.toDate(ts);

        //     // var date = new Date();      // (1970, 0, 1); // Epoch
        //     // date.setSeconds(seconds);
        //     return date;
        //     // var updated_at = Timestamp.fromDate(new Date());
        // } else {
        const dt: Date = timestamp.toDate();
        return dt;
        // }
    }

    public static parse(datetime: string, format: string, setToMidnight = true): TTimestamp {
        let date;

        if (format === 'iso') {
            date = parseISO(datetime);
        } else {
            date = parse(datetime, format, new Date());
        }
        let timestamp = this.toTimestamp(date) as TTimestamp;
        if (setToMidnight) {
            timestamp = this.setHoursMinutesSeconds(timestamp, 23, 59, 59);
        }
        return timestamp;
    }

    public static timestampToUTC(timestamp: TTimestamp): string {
        // const str = this.formatTimestamp(timestamp, DateTimeFormats.UTC);
        const date = this.formatTimestamp(timestamp, DateTimeFormats.ShortDate);
        const time = this.formatTimestamp(timestamp, DateTimeFormats.ShortTime1);
        const str = date + 'T' + time;
        return str;
        // const dateFromIonDatetime = '2021-06-04T14:23:00-04:00';
        // const formattedString = format(parseISO(dateFromIonDatetime), 'MMM d, yyyy');
    }

    // The incoming utcDatetime must be in this format:  DateTimeFormats.UTC = 'yyyy-MM-ddTHH:mm:ss'
    public static UTCToTimestamp(utcDatetime: string): TTimestamp {
        // const dateFromIonDatetime = '2021-06-04T14:23:00-04:00';
        const date: Date = parseISO(utcDatetime);
        const timestamp = this.toTimestamp(date) as TTimestamp;
        return timestamp;
    }

    public static isBeforeToday(timestamp: TTimestamp): boolean {
        // console.error('dateService.isBeforeToday() is incomplete.');
        const yesterdayTs = this.addToNow(-1, 'days', true);
        // const yesterdayFmt  = this.formatTimestamp(yesterdayTs, DateTimeFormats.ShortDate);
        const yesterdayDate = this.toDate(yesterdayTs);

        const date = this.toDate(timestamp);
        // const dateFmt = this.formatTimestamp(yesterdayTs, DateTimeFormats.ShortDate);
        const after = isAfter(yesterdayDate, date);
        return after;
    }

    public static isBeforeNow(timestamp: TTimestamp): boolean {
        const date = this.toDate(timestamp);
        const now = new Date();
        const after = isAfter(now, date);
        return after;
    }

    public static isAfterNow(timestamp: TTimestamp): boolean {
        const date = this.toDate(timestamp);
        const now = new Date();
        const after = isAfter(date, now);
        return after;
    }

    public static formatTimestamp(timestamp: TTimestamp | string, pattern: string = DateTimeFormats.Full): string {
        let date;
        let dateString;
        let ts;

        if (!timestamp) {
            // console.log('formatTimestamp called with invalid timestamp');
            return '';
        }

        switch (typeof timestamp) {
            case 'string':
                if (timestamp === 'now') {
                    date = new Date();
                }
                break;

            case 'number':
                // const ts = this.toTimestamp(timestamp) as TTimestamp;
                ts = this.toTimestamp(timestamp) as TTimestamp;
                date = this.toDate(ts);
                break;

            case 'object':
                date = this.toDate(timestamp);
                break;

            default:
                // console.log('formatTimestamp called with invalid timestamp');
                break;
        }

        if (pattern === 'iso') {
            dateString = formatISO(date!);
        } else {
            dateString = format(date!, pattern);
        }
        return dateString;
    }

    public static addToNow(duration: number, unit: string, setToMidnight = true): TTimestamp {
        const now = this.now();
        const newTimestamp = this.add(now, duration, unit, setToMidnight);
        return newTimestamp;
    }

    public static add(timestamp: TTimestamp, duration: number, unit: string, setToMidnight = true): TTimestamp {
        const date = this.toDate(timestamp);

        let newDate: Date;
        switch (unit) {
            case 'seconds':
                newDate = addSeconds(date, duration);
                break;

            case 'minutes':
                newDate = addMinutes(date, duration);
                break;

            case 'hours':
                newDate = addHours(date, duration);
                break;

            case 'days':
                newDate = addDays(date, duration);
                break;

            case 'months':
                newDate = addMonths(date, duration);
                break;

            case 'years':
                newDate = addYears(date, duration);
                break;

            default:
                throw new Error('Invalid unit passed to DateService.add()');
        }

        let newTimestamp = this.toTimestamp(newDate);
        if (setToMidnight) {
            newTimestamp = this.setHoursMinutesSeconds(newTimestamp, 23, 59, 59);
        }
        return newTimestamp;
    }

    public static setTimestampToStartOfDay(timestamp: TTimestamp): TTimestamp {
        return this.setHoursMinutesSeconds(timestamp, 0, 0, 0);
    }

    public static setTimestampToEndOfDay(timestamp: TTimestamp): TTimestamp {
        return this.setHoursMinutesSeconds(timestamp, 23, 59, 59);
    }

    public static setHoursMinutesSeconds(timestamp: TTimestamp, numHours: number, numMinutes: number,
        numSeconds: number): TTimestamp {
        let date = this.toDate(timestamp);
        date = setHours(date, numHours);
        date = setMinutes(date, numMinutes);
        date = setSeconds(date, numSeconds);

        const newTimestamp = this.toTimestamp(date);
        return newTimestamp;
    }

    public static adjustTimeBoundary(timestamp: TTimestamp, slot: 'start' | 'end'): TTimestamp {
        let ts: TTimestamp;

        switch (slot) {
            case 'start':
                ts = this.setHoursMinutesSeconds(timestamp, 0, 0, 0);
                break;

            case 'end':
                ts = this.setHoursMinutesSeconds(timestamp, 23, 59, 59);
                break;
        }
        return ts;
    }

    public static get currentDate(): string {
        const str = this.formatTimestamp('now', 'PPP'); // 'LL';
        return str;
    }

    // CurrentTime
    public static get currentTime(): string {
        const str = this.formatTimestamp('now', 'h:mm a');
        return str;
    }

    // was Called by orders.ts and order.ts
    // public static reformatDateTime(datetime: string): string {
    //     // // 2021-04-09 14:47:32.000000
    //     // const parsed = mom-ent(datetime, 'YYYY-MM-DD hh:mm:ss').format('MMM DD, YYYY - hh:mm a');
    //     // return parsed;
    //     return '';
    // }

    public static getDateValue(/* datetime: string */): number {
        // // 2021-12-11 14:47:32.000000
        // const value = mom-ent(datetime, 'YYYY-MM-DD hh:mm:ss').valueOf();
        // return value;
        return 0;
    }

    public static formatTimeAndDuration(start: TTimestamp, end: TTimestamp): string {
        const timeStart = this.formatTimestamp(start, DateTimeFormats.LongDate);
        // const timeEnd = this.formatTimestamp(end, DateTimeFormats.LongDate);
        const duration = this.formatDuration(start, end);
        const str = timeStart + ' (+' + duration + ')';
        return str;

        // const timeStart = this.formatTime(start);
        // const timeEnd = this.formatTime(end);
        // const duration = this.formatDuration(start, end);
        // const str = timeStart + ' (+' + duration + ')';
        // return str;
    }

    // public static computeDuration(start: TTimestamp, end: TTimestamp): Duration {
    public static computeDuration(start: TTimestamp, end: TTimestamp): Duration {
        const startDate = this.toDate(start);
        const endDate = this.toDate(end);
        const interval = { 'start': startDate, 'end': endDate };
        const duration = intervalToDuration(interval);
        return duration;
    }

    // Compare 11 February 1987 and 10 July 1989:
    // const result = compareAsc(new Date(1987, 1, 11), new Date(1989, 6, 10))
    // => -1

    // TODO: Changed type from any to Duration
    // public static compareDuration(duration1: a-n-y, duration2: a-n-y): number {
    public static compareDuration(duration1: Duration, duration2: Duration): number {
        const iso1 = formatISODuration(duration1);
        const timestamp1 = this.parse(iso1, 'iso', false);
        const date1 = this.toDate(timestamp1);

        const iso2 = formatISODuration(duration2);
        const timestamp2 = this.parse(iso2, 'iso', false);
        const date2 = this.toDate(timestamp2);

        const result = compareAsc(date1, date2);
        return result;
    }

    public static formatDuration(start: TTimestamp, end: TTimestamp): string {
        if (!start || !end) {
            return '';
        }
        const startDate = this.toDate(start);
        const endDate = this.toDate(end);
        const interval = { 'start': startDate, 'end': endDate };
        const duration = intervalToDuration(interval);
        let str = formatDuration(duration);

        // In practice, we will never encounter a ticket duration with units of years, months, weeks or days.
        // So the replacement of those units are here for completeness sake.
        str = str.replace(' years', 'y');
        str = str.replace(' months', 'm');
        str = str.replace(' weeks', 'w');
        str = str.replace(' days', 'd');
        str = str.replace(' hours', 'h');
        str = str.replace(' minutes', 'm');
        str = str.replace(' seconds', 's');

        return str;

        // Might be useful.
        // const numMins = Math.floor(seconds / 60);
        // const numHours = Math.floor(numMins / 60);
        // const numSecs = seconds - (numHours * 60 * 60) - (numMins * 60);
        // let str = '';
        // if (numHours > 1) {
        //     str += numHours.toString() + 'h';
        // }
        // if (numMins > 1) {
        //     str += numMins.toString() + 'm';
        // }
        // str += numSecs.toString() + 's';
        // return str
        // const numMinsRounded = Math.round(numMins * 10) / 10;
        // return numMinsRounded.toString();
        // const xxx = Math.round((numSecs * 60.0) / 60.0);
        // const yyy = Math.round(numSecs * 60.0 / 60.0);
        // const numMins1 = Math.round((numSecs * 60.0) / 60);
        // const numMins2 = Math.round(numSecs * 60.0) / 60;
        // var rounded = Math.round( number * 10 ) / 10;
        // const str = mend.diff(mstart, 'minutes').toString();
        // return str;
    }

    public static datesEqual(ts1: TTimestamp, ts2: TTimestamp): boolean {
        if (!ts1 || !ts2) {
            return false;
        }
        const date1 = this.formatTimestamp(ts1, DateTimeFormats.ShortDate);
        const date2 = this.formatTimestamp(ts2, DateTimeFormats.ShortDate);
        const datesEqual = (date1 === date2);

        return datesEqual;
    }

    public static getReadableDate(ts1: TTimestamp, format: string = DateTimeFormats.All): string {
        const isMinimum = this.isMinimumDate(ts1);

        // Apparently, in the Firebase Console when a Timestamp is set to minimum (Dec 31, 1969 4:00PM),
        // then in the typescript code we can compare this date to undefined!
        if (!ts1 || isMinimum) {
            return 'Unknown';
        }

        const str = this.formatTimestamp(ts1, format);
        return str;
    }

    // When this code is called from a Firebase Function it will return the server's timezone.
    // When this code is called from the client side it will return the local timezone.
    public static getTimezone(): string {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        // console.log('timezone = ', timezone);
        return timezone;
    }

    // Will this work???
    public static setLocalTimezone(timezone: string): void {
        DateImplService.localTimezone = timezone;
    }

    public static convertServerTimestampToToLocalTime(serverTimestamp: TTimestamp, localTimezone: string): void { // TTimestamp {
        // console.log('localTimezone = ', localTimezone);
        // const dt = zonedTimeToUtc('2020-10-13', 'America/Chicago');
        // console.log('dt = ', dt);
    }

    public static isTimestampToday(ts: TTimestamp): boolean {
        const today = new Date();
        const dt = this.toDate(ts);
        const isToday = dt.getDate() === today.getDate() &&
            dt.getMonth() === today.getMonth() &&
            dt.getFullYear() === today.getFullYear();
        return isToday;
    }

    // Usage: const dateString = this.convertUtcToLocalTime('2022-09-20T18:00:00Z');
    // private convertUtcToLocalTime(utcTime: string): string {
    //     const date = new Date(utcTime);
    //     const zonedDate = utcToZonedTime(date, Intl.DateTimeFormat().resolvedOptions().timeZone);
    //     return zonedDate.toString();
    // }

    //    public static convertServerTimeToLocalTime(timestamp: TTimestamp): a-n-y { // TTimestamp {
    //     // public static convertTimestampToToServerTime(): void { // TTimestamp {
    //         const tz = this.getTimezone();
    //         const date = this.toDate(timestamp);

    //         const dt1 = zonedTimeToUtc(date, 'America/Los_Angeles');
    //         const dt2 = zonedTimeToUtc(date, 'America/Denver');
    //         const dt3 = zonedTimeToUtc(date, 'America/Chicago');
    //         const dt4 = zonedTimeToUtc(date, 'America/New_York');

    //         const dt5 = utcToZonedTime(date, 'America/Los_Angeles');
    //         const dt6 = utcToZonedTime(date, 'America/Denver');
    //         const dt7 = utcToZonedTime(date, 'America/Chicago');
    //         const dt8 = utcToZonedTime(date, 'America/New_York');

    //         // const dt = zonedTimeToUtc('2020-10-13', 'America/Chicago');

    //         // const utcDate = zonedTimeToUtc('2018-09-01 18:01:36.386', 'Europe/Berlin')

    //         // Obtain a Date instance that will render the equivalent Berlin time for the UTC date
    //         // const date = new Date('2018-09-01T16:01:36.386Z')
    //         // const timeZone = 'Europe/Berlin'
    //         // const zonedDate = utcToZonedTime(date, timeZone)
    //         // zonedDate could be used to initialize a date picker or display the formatted local date/time

    //         // Set the output to "1.9.2018 18:01:36.386 GMT+02:00 (CEST)"
    //         // const pattern = 'd.M.yyyy HH:mm:ss.SSS \'GMT\' XXX (z)'
    //         // const output = format(zonedDate, pattern, { timeZone: 'Europe/Berlin' })
    //     }

    // https://stackoverflow.com/questions/58561169/date-fns-how-do-i-format-to-utc
    // public static convertGMTToLocalTimezone_ORIGINAL() {
    //     // import { parseISO } from "date-fns";
    //     // import { format, utcToZonedTime } from "date-fns-tz";

    //     const time = "2019-10-25T08:10:00Z";

    //     const parsedTime = parseISO(time);
    //     console.log(parsedTime); // 2019-10-25T08:10:00.000Z

    //     const formatInTimeZone = (date, fmt, tz) =>
    //     format(utcToZonedTime(date, tz),
    //             fmt,
    //             { timeZone: tz });

    //     const formattedTime = formatInTimeZone(parsedTime, "yyyy-MM-dd kk:mm:ss xxx", "UTC");
    //     console.log(formattedTime); // 2019-10-25 08:10:00 +00:00
    // }

    // commented methods test() and convertUtcDatetimeToLocalDatetime() below because of import error:
    // utcToZonedTime not found in 'date-fns-tz'eslintimport/named
    // '"date-fns-tz"' has no exported member named 'utcToZonedTime'. Did you mean 'toZonedTime'?

    /* public static test(): void {
        new Date() automatically gives a date expressed in the utc/gmt timezone.
        const utcDatetime = new Date().toISOString();

        const timezones = ['GMT', 'Pacific/Honolulu', 'America/Los_Angeles', 'America/New_York'];
        for (const timezone of timezones) {

            const zonedDate = utcToZonedTime(utcDatetime, timezone)
            const str = formatTZ(zonedDate, DateTimeFormats.Full);
            console.log({ timezone, str });
        }
    }

    public static convertUtcDatetimeToLocalDatetime(
        utcDatetime: string, format: string = DateTimeFormats.Full): unknown {
        const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const zonedDate = utcToZonedTime(utcDatetime, localTimezone);
        const timestamp = this.toTimestamp(zonedDate);
        const str = formatTZ(zonedDate, format);
        return { timestamp, str };
    }*/

    /*
    public static test(): void {
        // new Date() automatically gives a date expressed in the utc/gmt timezone.
        const utcDatetime = new Date().toISOString();

        const timezones = [ 'GMT', 'Pacific/Honolulu', 'America/Los_Angeles', 'America/New_York' ];
        for (const timezone of timezones) {
            const {timestamp, str} = this.convertUtcDatetimeToLocalDatetime(utcDatetime)
            // const zonedDate = utcToZonedTime(utcDatetime, timezone)
            // const str = formatTZ(zonedDate, DateTimeFormats.Full);
            console.log({timezone, str});
        }

    }

    */
    // public static convertGMTToLocalTimezone(utcDate: string, timezone: string) {
    //     // import { parseISO } from "date-fns";
    //     // import { format, utcToZonedTime } from "date-fns-tz";

    //     const time = "2019-10-25T08:10:00Z";

    //     const parsedTime = parseISO(time);
    //     console.log(parsedTime); // 2019-10-25T08:10:00.000Z

    //     // const formatInTimeZone = (date, fmt, timezone) =>
    //     //     format(utcToZonedTime(date, timezone),
    //     //         fmt,
    //     //         { timeZone: timezone });

    //     // const formatInTimeZone = (date, fmt, timezone) =>
    //     // format(utcToZonedTime(date, timezone),
    //     //     fmt,
    //     //     { timeZone: timezone });

    //     const formattedTime = formatInTimeZone(parsedTime, "yyyy-MM-dd kk:mm:ss xxx", "UTC");
    //     console.log(formattedTime); // 2019-10-25 08:10:00 +00:00
    // }
}
